// Create a project and start talking to Dennis about it — WITHOUT navigating.
//
// The dashboard's counterpart to `startProject.js`. That module creates a project
// and hands `ProjectPage` a `location.state` payload; this one creates a project
// and starts a SERVER agent run, leaving the user on the dashboard. `/learn` keeps
// using `startProject.js` unchanged — it stamps a template and (optionally) a
// guided tutorial locally, both of which need the editor mounted.
//
// A plain async function rather than a hook, deliberately: it needs no router, so
// `Dashboard.test.js`'s `react-router-dom` mock (which exposes only `useNavigate`)
// does not have to grow, and the call order below can be asserted directly.
//
// This also removes a real asymmetry. The composer's first prompt used to run
// through the BROWSER round loop (`pendingKickoff` → `runBackgroundWorkflowTurn`
// → `runAgentRounds`) while every later message went to the server run. Now both
// are server runs, so a pipeline keeps building with the tab closed from the very
// first turn.

import { createProject, summarizeProject, setProjectAgentMode } from '../api/projects';
import { MODE_AUTO, MODE_PLAN, normalizeMode } from '../panels/chatModes';
import { saveWorkflow } from '../api/workflow';
import { saveWorkflowAgentState } from '../api/workflowAgentState';
import { startAgentRun } from '../api/agentRun';
import { attachmentBriefLines } from '../constants/attachments';
import { buildKickoffMessage } from './agentKickoff';
import { DEFAULT_GROUP } from '../agent/useAgentRun';

// One retry on a not-yet-visible project. `launch.project_exists` reads with
// `ConsistentRead`, which closes this on the server side; the retry covers what a
// strongly-consistent read cannot (two requests landing on different endpoints).
// Short, because the alternative is telling the user their brand-new project does
// not exist.
const RETRY_DELAY_MS = 250;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The problem brief: what the user typed plus the names of anything they attached.
 *
 * The attachment lines matter more than they look. The files are ALREADY in the
 * repository (the composer uploads on add), so they only need naming — and naming
 * them in the BRIEF, which the backend injects into every agent prompt, is what
 * lets the agent's existing `list_available_files` / `set_cad_file` tools find the
 * part on turn one and still know about it ten messages later.
 */
export function buildProblemBrief(context, attachments) {
  return [context, attachmentBriefLines(attachments)].filter(Boolean).join('\n\n');
}

/**
 * @returns {Promise<{project, run, error}>} — `project` is set whenever creation
 *   succeeded, even if the run did not start. Swallowing that would leave the user
 *   with an invisible project: it exists, it is in the rail, and nothing said so.
 */
export async function startConversation({
  name,
  context = '',
  unitSystemId,
  attachments = [],
  // The composer's own starting selection, not `chatModes.DEFAULT_AGENT_MODE` (still
  // `auto`, and still what an unknown or absent stored mode reads as). Every real
  // caller passes this explicitly; the fallback only keeps the two in step.
  agentMode = MODE_PLAN,
}) {
  const project = await createProject(name, context);
  const projectId = project.projectId;

  // ORDER IS LOAD-BEARING from here.
  //
  // 1. The unit system rides the workflow PUT, and with no navigation there is no
  //    `ProjectPage.handleUnitSystemConfirm` to write it. The graph is empty at
  //    this instant, which is exactly why it must go FIRST: once the run starts
  //    the server owns `workflowNodes`, and a blank-graph PUT arriving after the
  //    agent's first `create_tile` would wipe the pipeline it just built. This is
  //    the dashboard-side echo of ProjectPage suspending autosave during a run.
  await saveWorkflow(projectId, [], [], unitSystemId);

  // 2. The brief must exist before turn one. `chat.build_workflow_prompt` reads
  //    `workflowAgentState.problemBrief` on EVERY turn, so a brief that lands
  //    after the run starts is a brief the opening round never saw — and the
  //    opening round is the one that decides which pipeline to build.
  const problemBrief = buildProblemBrief(context, attachments);
  if (problemBrief) {
    await saveWorkflowAgentState(projectId, { problemBrief });
  }

  // 3. The permission mode, when it is not the default.
  //
  //    Written to the project ROW because the mode is STICKY — the user picked it
  //    for this piece of work, and it must still be in force for the run after
  //    this one. It is also passed on `/start` below, and both are needed for
  //    different reasons: the row makes it durable, the request parameter makes
  //    TURN ONE gated regardless of whether this write has propagated by the time
  //    `launch_run` reads the row.
  //
  //    Not awaited-and-abandoned like `summarizeProject`: a mode that failed to
  //    persist means the NEXT run acts unattended when the user asked it not to.
  //    But not fatal either — the run's own copy still holds for this run, so a
  //    failure is reported rather than losing the conversation.
  //    The skip is against MODE_AUTO because that is what the BACKEND ROW defaults
  //    to (`projects.py`'s `agentMode: str = "auto"`), so patching it would be a
  //    request whose only outcome is nothing. It is NOT the composer's default any
  //    more — a composer-created project is Plan, so this write now fires on the
  //    ordinary path rather than the exceptional one.
  const mode = normalizeMode(agentMode);
  let modeError = null;
  if (mode !== MODE_AUTO) {
    try {
      await setProjectAgentMode(projectId, mode);
    } catch (err) {
      modeError = err.message || String(err);
    }
  }

  // 4. Now start the run.
  //
  //    No `workflowId`, and `groupId: '__default__'` — the same pair
  //    `ProjectPage.handleServerSend` sends for the implicit group. That is what
  //    makes the dashboard thread and the project's own workflow chat two views of
  //    ONE run rather than two runs on one canvas: `useAgentRun`'s `groupKeyOf`
  //    maps a null `workflowId` onto `DEFAULT_GROUP` on both sides.
  //    The kickoff scaffolding is for the MODEL, not the user. `displayMessage` is
  //    what the transcript shows — the problem brief, i.e. what they actually typed
  //    plus any attachment lines — while `message` keeps the wording that tells the
  //    agent to work back from the result and start the pipeline. Without the split
  //    the user reads "The canvas is empty. Work out what result this needs…" as
  //    their own opening message.
  const message = buildKickoffMessage({ context: problemBrief || context });
  const displayMessage = problemBrief || context;
  const body = { message, displayMessage, groupId: DEFAULT_GROUP, agentMode: mode };
  let run = null;
  let error = null;
  try {
    run = await startAgentRun(projectId, body);
  } catch (err) {
    if (err?.status === 404) {
      // The project's own write has not propagated yet. See RETRY_DELAY_MS.
      await wait(RETRY_DELAY_MS);
      try {
        run = await startAgentRun(projectId, body);
      } catch (retryErr) {
        error = retryErr.message || String(retryErr);
      }
    } else {
      error = err.message || String(err);
    }
  }

  // Deliberately NOT awaited, unchanged from `startProject.js`: its only effect is
  // to replace the locally-derived title with a better one on the next dashboard
  // fetch, so a failure (no org key, a 502) simply leaves the fallback in place.
  if (context.trim()) summarizeProject(projectId).catch(() => {});

  // `modeError` is reported SEPARATELY from `error`: the run started and is gated
  // as asked, so this is not a failed conversation — only a warning that the choice
  // did not stick for the next one.
  return { project, run, error, modeError };
}
